import { z } from 'zod'

/**
 * The Übersicht summary.
 *
 * Every number here also appears somewhere else in the app, which is the whole
 * risk: a dashboard that quietly disagrees with the page it summarises is worse
 * than no dashboard. So the SCOPE is defined here, once, and both the server
 * that counts and the table that lists import it from the same place.
 */

/**
 * Marketing phases where chasing documents is worthwhile — the out-of-the-box
 * scope of the Unterlagen page.
 */
export const CHECKLIST_ACTIVE_STATUSES = ['Aktive Vermarktung', 'Vorbereitung'] as const

/**
 * The document checklist is purchase due diligence. Across the live account,
 * rental objects have never had a single box ticked, so counting them would
 * bury the real number under a permanent 23-of-23 for half the portfolio.
 */
export const CHECKLIST_MARKETING_TYPE = 'Kauf'

/** Shown on the card, so nobody has to guess which objects were counted. */
export const CHECKLIST_SCOPE_LABEL = `${CHECKLIST_MARKETING_TYPE} · ${CHECKLIST_ACTIVE_STATUSES.join(', ')}`

/** Who started a sync: a person on the Unterlagen page, or the schedule. */
export const syncTriggerSchema = z.enum(['manual', 'scheduled'])

export type SyncTrigger = z.infer<typeof syncTriggerSchema>

export const SYNC_TRIGGER_LABELS: Record<SyncTrigger, string> = {
  manual: 'manuell',
  scheduled: 'automatisch',
}

export const syncSummarySchema = z.object({
  status: z.enum(['running', 'succeeded', 'failed']),
  trigger: syncTriggerSchema,
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  recordCount: z.number().int().nonnegative(),
  error: z.string().nullable(),
})

export const dashboardSummarySchema = z.object({
  /** Null before the first sync has ever run. */
  lastSync: syncSummarySchema.nullable(),

  estates: z.object({
    /** Everything in the local mirror, every marketing phase. */
    mirrored: z.number().int().nonnegative(),
    /** Only those the document checklist actually applies to. */
    inScope: z.number().int().nonnegative(),
  }),

  documents: z.object({
    scopeLabel: z.string(),
    open: z.number().int().nonnegative(),
    done: z.number().int().nonnegative(),
    notApplicable: z.number().int().nonnegative(),
    /** Distinct document types in the checklist — 23 on the live account. */
    types: z.number().int().nonnegative(),
    estatesWithOpen: z.number().int().nonnegative(),
    estatesComplete: z.number().int().nonnegative(),
  }),

  handovers: z.object({
    draft: z.number().int().nonnegative(),
    completed: z.number().int().nonnegative(),
  }),

  imageAi: z.object({
    configured: z.boolean(),
    active: z.number().int().nonnegative(),
    succeeded: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
  }),
})

export type DashboardSummary = z.infer<typeof dashboardSummarySchema>
export type SyncSummary = z.infer<typeof syncSummarySchema>

/**
 * `GET /checklists/sync/status` — what the Unterlagen page and the settings
 * card show about the sync without triggering one. `nextRunAt` is computed on
 * the server, because "06:00 in Berlin" is a timezone question the browser
 * must not answer differently from the scheduler; null when the schedule is
 * off or onOffice is not configured.
 */
export const syncStatusSchema = z.object({
  /** A run is in progress right now — the page disables its own button. */
  running: z.boolean(),
  lastRun: syncSummarySchema.nullable(),
  nextRunAt: z.iso.datetime().nullable(),
})

export type SyncStatus = z.infer<typeof syncStatusSchema>
